import { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Activity, RefreshCw, Shield, AlertTriangle, CheckCircle2, Bug, Clock, Zap } from 'lucide-react';

const ScoreBadge = ({ score }) => {
  const color = score >= 80 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400';
  const bg = score >= 80 ? 'bg-emerald-400/10' : score >= 50 ? 'bg-amber-400/10' : 'bg-red-400/10';
  const ring = score >= 80 ? 'ring-emerald-400/30' : score >= 50 ? 'ring-amber-400/30' : 'ring-red-400/30';
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-bold ${color} ${bg} ring-1 ${ring}`}>
      {score}%
    </span>
  );
};

const StatusDot = ({ status }) => {
  const colors = {
    ok: 'bg-emerald-400',
    repaired: 'bg-blue-400',
    warning: 'bg-amber-400',
    error: 'bg-red-400',
  };
  return <span className={`inline-block w-2 h-2 rounded-full ${colors[status] || 'bg-gray-400'}`} />;
};

export default function SanteSystemePage() {
  const [rapport, setRapport] = useState(null);
  const [historique, setHistorique] = useState([]);
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('live');

  const fetchData = useCallback(async () => {
    try {
      const [histRes, bugsRes] = await Promise.all([
        api.get('/auto-repair/historique?limit=30'),
        api.get('/auto-repair/bugs'),
      ]);
      setHistorique(histRes.data?.reports || []);
      setBugs(bugsRes.data?.bugs || []);
      if (histRes.data?.reports?.length > 0) {
        setRapport(histRes.data.reports[0]);
      }
    } catch (e) {
      console.error('Erreur chargement santé:', e);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const runManual = async () => {
    setLoading(true);
    try {
      const res = await api.post('/auto-repair/run');
      setRapport(res.data);
      await fetchData();
    } catch (e) {
      console.error('Erreur:', e);
    }
    setLoading(false);
  };

  const lastScore = rapport?.score ?? '--';
  const lastChecks = rapport?.nb_checks ?? rapport?.checks?.length ?? 0;
  const lastIssues = rapport?.nb_issues ?? rapport?.issues?.length ?? 0;
  const lastRepairs = rapport?.nb_repairs ?? rapport?.repairs?.length ?? 0;

  return (
    <div className="min-h-screen bg-[#0a0f1a] text-gray-100 p-6" data-testid="sante-systeme-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
            <Activity className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Gardien du Systeme</h1>
            <p className="text-sm text-gray-500">Auto-repair permanent | Toutes les 15 min</p>
          </div>
        </div>
        <Button
          onClick={runManual}
          disabled={loading}
          className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"
          data-testid="run-repair-btn"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Lancer un diagnostic
        </Button>
      </div>

      {/* Score cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Card className="bg-[#111827] border-gray-800">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Score</p>
            <div className="text-3xl font-black">
              <ScoreBadge score={lastScore} />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-[#111827] border-gray-800">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Checks</p>
            <div className="text-3xl font-black text-blue-400">{lastChecks}</div>
          </CardContent>
        </Card>
        <Card className="bg-[#111827] border-gray-800">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Problemes</p>
            <div className={`text-3xl font-black ${lastIssues > 0 ? 'text-red-400' : 'text-emerald-400'}`}>{lastIssues}</div>
          </CardContent>
        </Card>
        <Card className="bg-[#111827] border-gray-800">
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Reparations</p>
            <div className={`text-3xl font-black ${lastRepairs > 0 ? 'text-blue-400' : 'text-gray-500'}`}>{lastRepairs}</div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-800 pb-2">
        {[
          { key: 'live', label: 'Diagnostic Live', icon: Zap },
          { key: 'historique', label: 'Historique', icon: Clock },
          { key: 'bugs', label: 'Base de Connaissances', icon: Bug },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-emerald-600/20 text-emerald-400' : 'text-gray-500 hover:text-gray-300 hover:bg-gray-800'
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* Live Diagnostic */}
      {tab === 'live' && rapport && (
        <div className="space-y-4">
          <Card className="bg-[#111827] border-gray-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-base text-gray-300 flex items-center gap-2">
                <Shield className="w-4 h-4 text-emerald-400" />
                Dernier diagnostic - {rapport.date ? new Date(rapport.date).toLocaleString('fr-FR') : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {(rapport.checks || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-800/50 last:border-0">
                    <div className="flex items-center gap-2">
                      <StatusDot status={c.status} />
                      <span className="text-sm">{c.check || c.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">{c.detail?.substring(0, 80)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {(rapport.issues?.length > 0) && (
            <Card className="bg-red-950/20 border-red-900/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Problemes detectes
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rapport.issues.map((issue, i) => (
                  <div key={i} className="text-sm text-red-300 py-1">
                    {issue.check || issue.name}: {issue.detail}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {(rapport.repairs?.length > 0) && (
            <Card className="bg-blue-950/20 border-blue-900/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-blue-400 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  Reparations effectuees
                </CardTitle>
              </CardHeader>
              <CardContent>
                {rapport.repairs.map((r, i) => (
                  <div key={i} className="text-sm text-blue-300 py-1">{r}</div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Historique */}
      {tab === 'historique' && (
        <Card className="bg-[#111827] border-gray-800">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-800 text-gray-500 text-xs uppercase">
                  <th className="text-left p-3">Date</th>
                  <th className="text-center p-3">Score</th>
                  <th className="text-center p-3">Checks</th>
                  <th className="text-center p-3">Issues</th>
                  <th className="text-center p-3">Repairs</th>
                </tr>
              </thead>
              <tbody>
                {historique.map((h, i) => (
                  <tr key={i} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition-colors">
                    <td className="p-3 text-gray-400">{new Date(h.date).toLocaleString('fr-FR')}</td>
                    <td className="p-3 text-center"><ScoreBadge score={h.score} /></td>
                    <td className="p-3 text-center text-blue-400">{h.nb_checks}</td>
                    <td className="p-3 text-center">
                      <span className={h.nb_issues > 0 ? 'text-red-400 font-bold' : 'text-gray-500'}>{h.nb_issues}</span>
                    </td>
                    <td className="p-3 text-center">
                      <span className={h.nb_repairs > 0 ? 'text-blue-400 font-bold' : 'text-gray-500'}>{h.nb_repairs}</span>
                    </td>
                  </tr>
                ))}
                {historique.length === 0 && (
                  <tr><td colSpan={5} className="p-8 text-center text-gray-600">Aucun historique disponible</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {/* Bugs Knowledge */}
      {tab === 'bugs' && (
        <div className="space-y-3">
          {bugs.length === 0 && (
            <Card className="bg-[#111827] border-gray-800">
              <CardContent className="p-8 text-center text-gray-600">
                Aucun bug enregistre. Le systeme est sain.
              </CardContent>
            </Card>
          )}
          {bugs.map((b, i) => (
            <Card key={i} className="bg-[#111827] border-gray-800">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Bug className="w-4 h-4 text-amber-400" />
                      <span className="font-mono text-sm font-bold text-gray-200">{b.bug_type}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        b.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                        b.severity === 'high' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>{b.severity}</span>
                    </div>
                    <p className="text-sm text-gray-400">{b.description}</p>
                    <div className="flex gap-4 mt-2 text-xs text-gray-600">
                      <span>Occurrences: <strong className="text-gray-400">{b.occurrence_count}</strong></span>
                      <span>Auto-fixe: <strong className="text-emerald-400">{b.auto_fixed_count || 0}x</strong></span>
                      <span>Vu: {b.last_seen ? new Date(b.last_seen).toLocaleDateString('fr-FR') : '-'}</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
